import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PencilSimple, DownloadSimple, FloppyDisk, Eye, EyeSlash, UploadSimple, ListBullets, CaretDown, CaretUp } from "@phosphor-icons/react";
import api, { API, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

// Resize an uploaded clinical photo client-side. Preserves aspect ratio
// (unlike profile photos which we square-crop). Max 1200px on the longest
// side, JPEG q=0.85 - keeps clinical detail crisp while keeping the doc
// under ~200KB.
async function quizImageToDataUrl(file, maxSide = 1200, quality = 0.85) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = () => rej(new Error("Could not read the image"));
    r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = () => rej(new Error("Could not decode the image"));
    i.src = dataUrl;
  });
  const ratio = Math.min(1, maxSide / Math.max(img.width, img.height));
  const w = Math.round(img.width * ratio);
  const h = Math.round(img.height * ratio);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

const ANSWER_LABELS = {
  accidental: "Accidental",
  non_accidental: "Non-accidental",
  social_media: "Social media trend",
  infection: "Infection / pathogen",
};

export default function QuizAdmin() {
  const [summary, setSummary] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ prompt: "", clinical_history: "", image_url: "" });
  const [uploading, setUploading] = useState(false);
  const imgFileRef = useRef(null);
  const [publishDialog, setPublishDialog] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showSubmissions, setShowSubmissions] = useState(false);

  const load = async () => {
    try {
      const [s, c] = await Promise.all([api.get("/admin/quiz/summary"), api.get("/quiz/config")]);
      setSummary(s.data);
      setFeedback(c.data.feedback || "");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const togglePublish = async () => {
    setPublishing(true);
    try {
      const next = !summary.published;
      await api.post("/admin/quiz/publish", { published: next });
      toast.success(next ? "Quiz is now live to delegates" : "Quiz hidden from delegates");
      setPublishDialog(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPublishing(false); }
  };

  const startEdit = async (q) => {
    setEditing(q);
    // Initialise with empty image_url; if the question has an image stored,
    // lazy-fetch it so the user sees a preview without forcing the bulk
    // /admin/quiz/summary endpoint to ship every image up-front.
    setForm({ prompt: q.prompt || "", clinical_history: q.clinical_history || "", image_url: "" });
    if (q.has_image) {
      try {
        const { data } = await api.get(`/quiz/questions/${q.id}/image`);
        setForm((f) => ({ ...f, image_url: data.image_url || "" }));
      } catch { /* preview is best-effort; admin can re-upload */ }
    }
  };

  const onUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const url = await quizImageToDataUrl(file, 1200, 0.85);
      setForm((f) => ({ ...f, image_url: url }));
      toast.success("Image ready - click Save to keep it");
    } catch (err) {
      toast.error(err.message || "Could not process the image");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  const saveEdit = async () => {
    try {
      await api.patch(`/admin/quiz/questions/${editing.id}`, form);
      toast.success(`Question ${editing.number} updated`);
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveFeedback = async () => {
    setFeedbackSaving(true);
    try {
      await api.patch("/admin/quiz/feedback", { body: feedback });
      toast.success("Answers & learning points saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setFeedbackSaving(false); }
  };

  const downloadCsv = () => {
    fetch(`${API}/admin/quiz/responses.csv`, { credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u; a.download = "dftb26-quiz-responses.csv"; a.click();
        URL.revokeObjectURL(u);
      });
  };

  if (!summary) return <div className="text-center text-[#7A828C] py-6">Loading…</div>;

  return (
    <div data-testid="quiz-admin" className="space-y-5">
      <div
        data-testid="quiz-publish-banner"
        className={`p-4 border rounded-sm flex items-center gap-4 flex-wrap ${summary.published ? "border-[#A8D5BA] bg-[#EAF7EE]" : "border-[#E8D58A] bg-[#FFF8E5]"}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`font-mono-time text-[10px] uppercase tracking-widest ${summary.published ? "text-[#1f6b3a]" : "text-[#7a5a00]"}`}>
              {summary.published ? "Live to delegates" : "Hidden from delegates"}
            </span>
            <span className={`inline-block w-2 h-2 rounded-full ${summary.published ? "bg-[#1f6b3a]" : "bg-[#b08a00]"}`} aria-hidden="true" />
          </div>
          <p className="text-sm text-[#1c1c24] leading-snug">
            {summary.published
              ? "Delegates can see the quiz card on the Info tab and take it now."
              : "The quiz is hidden. Upload all 10 images & histories, then publish when you're ready."}
          </p>
        </div>
        <Button
          onClick={() => setPublishDialog(true)}
          className={`rounded-sm ${summary.published ? "bg-[#7A828C] text-[#FFFFFF] hover:bg-[#5a626c]" : "bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]"}`}
          data-testid="quiz-publish-toggle"
        >
          {summary.published ? <><EyeSlash size={14} className="mr-1" /> Hide from delegates</> : <><Eye size={14} className="mr-1" /> Publish quiz</>}
        </Button>
        <a
          href="/quiz?preview=1"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center h-10 px-3 rounded-sm border border-[#000000] text-[#000000] hover:bg-[#E6F8FC] text-sm font-medium transition-colors"
          data-testid="quiz-admin-preview-link"
        >
          <Eye size={14} className="mr-1.5" /> Preview as delegate
        </a>
      </div>

      <div className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C]">Submissions</div>
          <div className="font-display text-3xl text-[#000000]" data-testid="quiz-submission-count">{summary.total_submissions}</div>
        </div>
        <Button onClick={() => setShowSubmissions(true)} variant="outline" className="rounded-sm" data-testid="quiz-view-submissions">
          <ListBullets size={14} className="mr-1" /> View submissions
        </Button>
        <Button onClick={downloadCsv} variant="outline" className="rounded-sm" data-testid="quiz-export-csv">
          <DownloadSimple size={14} className="mr-1" /> Export CSV
        </Button>
      </div>

      <SubmissionsDialog open={showSubmissions} onOpenChange={setShowSubmissions} />

      <div className="space-y-3">
        <h3 className="font-display text-base">Questions</h3>
        <p className="text-xs text-[#7A828C]">Fill in the clinical history and image URL for each. The prompt defaults to the standard text - override per question if needed. Until every question has an image, the quiz shows a &quot;not yet available&quot; banner to delegates.</p>
        {summary.questions.map((q) => (
          <div key={q.id} className="p-3 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm" data-testid={`quiz-admin-q-${q.number}`}>
            <div className="flex items-start gap-3">
              <div className="w-20 h-20 rounded-sm bg-[#F0F2F7] overflow-hidden grid place-items-center flex-shrink-0">
                {q.has_image ? <LazyQuizThumb questionId={q.id} number={q.number} /> : <span className="text-[10px] text-[#7A828C] uppercase tracking-widest">No image</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-sm">Q{q.number}</div>
                <div className="text-xs text-[#5a5a5a] line-clamp-2">{q.clinical_history || <span className="italic text-[#7A828C]">No clinical history yet</span>}</div>
                <div className="mt-2 flex items-center gap-3 text-xs flex-wrap">
                  {Object.entries(q.answer_counts || {}).map(([k, v]) => (
                    <span key={k} className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C]">
                      {ANSWER_LABELS[k]}: <strong className="text-[#1c1c24]">{v}</strong>
                    </span>
                  ))}
                </div>
              </div>
              <button onClick={() => startEdit(q)} className="w-10 h-10 grid place-items-center rounded-sm hover:bg-[#F0F2F7]" data-testid={`quiz-edit-q-${q.number}`}>
                <PencilSimple size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm">
        <h3 className="font-display text-base mb-1">Answers &amp; learning points</h3>
        <p className="text-xs text-[#7A828C] mb-2">Published to delegates after the conference. They&apos;ll see this on the quiz Complete page once they&apos;ve submitted. Leave blank to hide.</p>
        <Textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} rows={8} className="rounded-sm" placeholder={"Q1 - accidental injury\nLearning point: …\n\nQ2 - non-accidental\nLearning point: …"} data-testid="quiz-feedback-textarea" />
        <Button onClick={saveFeedback} disabled={feedbackSaving} className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24] mt-2" data-testid="quiz-feedback-save">
          <FloppyDisk size={14} className="mr-1" /> {feedbackSaving ? "Saving…" : "Save learning points"}
        </Button>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#FFFFFF] border-[#E5E9F0] max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Edit question {editing?.number}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label htmlFor="q-prompt">Prompt</Label>
                <Input id="q-prompt" value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })} className="rounded-sm mt-1" data-testid="quiz-form-prompt" />
              </div>
              <div>
                <Label htmlFor="q-history">Clinical history</Label>
                <Textarea id="q-history" value={form.clinical_history} onChange={(e) => setForm({ ...form, clinical_history: e.target.value })} rows={5} className="rounded-sm mt-1" placeholder="A 4-year-old presents with…" data-testid="quiz-form-history" />
              </div>
              <div>
                <Label>Image</Label>
                <input
                  ref={imgFileRef}
                  type="file"
                  accept="image/*"
                  onChange={onUploadImage}
                  className="hidden"
                  data-testid="quiz-form-image-input"
                />
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <Button
                    type="button"
                    onClick={() => imgFileRef.current?.click()}
                    disabled={uploading}
                    className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]"
                    data-testid="quiz-form-image-upload-btn"
                  >
                    <UploadSimple size={14} className="mr-1.5" />
                    {uploading ? "Processing…" : (form.image_url ? "Replace image" : "Upload image")}
                  </Button>
                  {form.image_url && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setForm({ ...form, image_url: "" })}
                      className="rounded-sm"
                      data-testid="quiz-form-image-clear-btn"
                    >
                      Clear
                    </Button>
                  )}
                </div>
                <p className="text-xs text-[#7A828C] mt-2">JPG, PNG, HEIC or WEBP from your phone or computer. Auto-resized to max 1200px.</p>
              </div>
              <details className="text-xs">
                <summary className="cursor-pointer text-[#7A828C] hover:text-[#000000]">…or paste an image URL instead</summary>
                <Input
                  id="q-image"
                  value={form.image_url?.startsWith("data:") ? "" : form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  placeholder="https://…"
                  className="rounded-sm mt-2 font-mono-time text-xs"
                  data-testid="quiz-form-image-url"
                />
                <p className="text-[10px] text-[#7A828C] mt-1">Must be a direct image link (must end .jpg/.png/.webp). Google Drive share URLs don&apos;t work.</p>
              </details>
              {form.image_url && (
                <img src={form.image_url} alt="preview" className="max-h-48 rounded-sm border border-[#E5E9F0] object-contain" />
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveEdit} className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]" data-testid="quiz-form-save"><FloppyDisk size={14} className="mr-1" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={publishDialog} onOpenChange={setPublishDialog}>
        <AlertDialogContent className="bg-[#FFFFFF] border-[#E5E9F0]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">
              {summary.published ? "Hide the quiz from delegates?" : "Publish the quiz to delegates?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#5a5a5a] leading-relaxed">
              {summary.published
                ? "The quiz CTA on the Info tab will disappear and delegates will no longer be able to submit. Existing submissions are kept."
                : "The quiz CTA will appear on the Info tab and delegates can submit immediately. Make sure all 10 questions have images & clinical histories before publishing."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="quiz-publish-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={togglePublish}
              disabled={publishing}
              className={`rounded-sm ${summary.published ? "bg-[#a83232] hover:bg-[#8a2929]" : "bg-[#000000] hover:bg-[#1c1c24]"} text-[#FFFFFF]`}
              data-testid="quiz-publish-confirm"
            >
              {publishing ? "Saving…" : (summary.published ? "Hide quiz" : "Publish now")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SubmissionsDialog({ open, onOpenChange }) {  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/admin/quiz/submissions")
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = (data?.submissions || []).filter((s) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (s.user_name || "").toLowerCase().includes(q)
      || (s.user_email || "").toLowerCase().includes(q)
      || (s.user_org || "").toLowerCase().includes(q);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFFFF] max-w-2xl" data-testid="quiz-submissions-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Quiz submissions {data ? `(${data.total})` : ""}</DialogTitle>
        </DialogHeader>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, email or organisation…"
          className="rounded-sm"
          data-testid="quiz-submissions-search"
        />
        {loading && <p className="text-sm text-[#7A828C] py-6 text-center">Loading…</p>}
        {!loading && data && filtered.length === 0 && (
          <p className="text-sm text-[#7A828C] py-6 text-center">
            {data.total === 0 ? "No submissions yet." : `No submissions match "${query}".`}
          </p>
        )}
        <div className="space-y-2">
          {filtered.map((s) => {
            const isOpen = expanded === s.id;
            const answered = s.answers.filter((a) => a.answer_key).length;
            const date = s.submitted_at ? new Date(s.submitted_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" }) : "—";
            return (
              <div key={s.id} className="bg-[#F8F8FB] rounded-sm" data-testid={`quiz-submission-${s.id}`}>
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : s.id)}
                  className="w-full text-left p-3 flex items-start gap-3 hover:bg-[#E6F8FC] rounded-sm transition-colors"
                  data-testid={`quiz-submission-toggle-${s.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-sm text-[#1c1c24] truncate">{s.user_name || s.user_email || "Anonymous"}</div>
                    <div className="text-xs text-[#7A828C] truncate">
                      {s.user_org && <span>{s.user_org} · </span>}
                      <span className="font-mono-time">{date}</span> · {answered}/{data?.questions_total ?? s.answers.length} answered
                    </div>
                  </div>
                  {isOpen ? <CaretUp size={16} className="text-[#7A828C] mt-1" /> : <CaretDown size={16} className="text-[#7A828C] mt-1" />}
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 space-y-1.5 border-t border-[#E5E9F0] pt-2">
                    {s.user_email && (
                      <div className="text-[11px] text-[#7A828C] font-mono-time">{s.user_email}</div>
                    )}
                    {s.answers.map((a) => (
                      <div key={a.question_id} className="flex gap-2 text-xs">
                        <span className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C] flex-shrink-0 mt-0.5">Q{a.number}</span>
                        <span className="font-medium text-[#1c1c24] flex-shrink-0">
                          {a.answer_key ? `${a.answer_key} - ${a.answer_label}` : <span className="text-[#7A828C] italic">no answer</span>}
                        </span>
                        {a.clinical_history && (
                          <span className="text-[#7A828C] line-clamp-1 flex-1 min-w-0">{a.clinical_history}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Lazy thumbnail for the admin Questions list so the bulk /admin/quiz/summary
// response stays small. Each tile self-fetches its own base64 image_url —
// avoids shipping all 10 multi-MB blobs in one response (Cloudflare 520 fix).
function LazyQuizThumb({ questionId, number }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let active = true;
    api.get(`/quiz/questions/${questionId}/image`)
      .then((r) => { if (active) setSrc(r.data.image_url || ""); })
      .catch(() => { if (active) setErr(true); });
    return () => { active = false; };
  }, [questionId]);
  if (err) return <span className="text-[10px] text-[#a83232] font-mono-time">!</span>;
  if (!src) return <span className="text-[10px] text-[#7A828C] uppercase tracking-widest">Loading…</span>;
  return <img src={src} alt={`Q${number}`} className="w-full h-full object-cover" />;
}

